import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, ArrowLeft, Sunrise, Sunset, Lock } from "lucide-react";

// KIRA Diary - Session 4: "My diary", the owner-only calendar read view.
// Reads REAL data ONLY via the owner-scoped route GET /api/diary/entries
// (the S1 seal guarantees it returns just the caller's own entries). This
// component is read-only: it never writes and never calls an admin/all-users
// route. Days with an entry get a coloured underbracket in the day's tone.

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";

// Private tone markers (not a score). good=green, steady=sage, hard=clay.
const TONE = {
  good:   { color: "#3ecf8e", label: "Good" },
  steady: { color: "#9caf88", label: "Steady" },
  hard:   { color: "#c17a54", label: "Hard" },
};

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const WEEKDAYS = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

const pad = (n) => String(n).padStart(2, "0");
const keyOf = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;

export default function MyDiaryCalendar() {
  const today = new Date();
  const [view, setView] = useState({ y: today.getFullYear(), m: today.getMonth() });
  const [byDate, setByDate] = useState({});
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);   // 'YYYY-MM-DD' | null
  const [convo, setConvo] = useState(null);          // 'morning' | 'evening' | null
  const [flagged, setFlagged] = useState(false);

  const flagToManager = async () => {
    const token = localStorage.getItem("token");
    try {
      await fetch(`${API_BASE}/api/diary/nudge/flag`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      setFlagged(true);
    } catch { /* non-blocking */ }
  };

  const daysInMonth = useMemo(() => new Date(view.y, view.m + 1, 0).getDate(), [view]);
  const startWeekday = useMemo(() => new Date(view.y, view.m, 1).getDay(), [view]);

  const load = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem("token");
    const from = keyOf(view.y, view.m, 1);
    const to = keyOf(view.y, view.m, daysInMonth);
    try {
      // OWNER-SCOPED route only. No admin/all-users endpoint exists or is called.
      const res = await fetch(`${API_BASE}/api/diary/entries?from=${from}&to=${to}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { setByDate({}); return; } // quiet: no error surface
      const rows = await res.json();
      const map = {};
      (Array.isArray(rows) ? rows : []).forEach((e) => { map[e.entry_date] = e; });
      setByDate(map);
    } catch {
      setByDate({});
    } finally {
      setLoading(false);
    }
  }, [view, daysInMonth]);

  useEffect(() => { load(); setSelected(null); setConvo(null); }, [load]);

  const step = (delta) => {
    setView((v) => {
      const d = new Date(v.y, v.m + delta, 1);
      return { y: d.getFullYear(), m: d.getMonth() };
    });
  };

  const monthHasEntries = Object.keys(byDate).length > 0;

  // ---- Full conversation view ----
  if (selected && convo) {
    const entry = byDate[selected];
    const turns = (convo === "morning" ? entry?.morning_conversation : entry?.evening_conversation) || [];
    const title = convo === "morning" ? "Login check-in" : "Logout check-in";
    return (
      <div style={wrap}>
        <button onClick={() => setConvo(null)} style={backLink}>
          <ArrowLeft size={14} /> Back to {selected}
        </button>
        <h3 style={{ color: "#fff", fontSize: 18, margin: "12px 0 4px" }}>{title}</h3>
        <p style={{ color: "rgba(255,255,255,0.4)", fontSize: 12, marginBottom: 18 }}>{selected}</p>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {Array.isArray(turns) && turns.length > 0 ? (
            turns.map((t, i) => {
              const isUser = t.role === "user";
              return (
                <div key={i} style={{ display: "flex", justifyContent: isUser ? "flex-end" : "flex-start" }}>
                  <div style={{ maxWidth: "78%", padding: "10px 14px", borderRadius: 14,
                    background: isUser ? "#12351f" : "#15181c",
                    border: "1px solid rgba(255,255,255,0.06)", color: "#e8e8e8", fontSize: 14, lineHeight: 1.5 }}>
                    {t.text}
                  </div>
                </div>
              );
            })
          ) : (
            <p style={quiet}>No conversation was recorded for this check-in.</p>
          )}
        </div>
      </div>
    );
  }

  // ---- Day panels view ----
  if (selected) {
    const entry = byDate[selected];
    const Panel = ({ icon, title, summary, felt, which, hasConvo }) => (
      <div style={panel}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          {icon}
          <span style={{ color: "#fff", fontSize: 15, fontWeight: 600 }}>{title}</span>
        </div>
        {summary || felt ? (
          <>
            {summary && <p style={{ color: "rgba(255,255,255,0.8)", fontSize: 14, lineHeight: 1.5 }}>{summary}</p>}
            {felt && <p style={{ color: "rgba(255,255,255,0.5)", fontSize: 13, marginTop: 6, fontStyle: "italic" }}>Felt: {felt}</p>}
            {hasConvo && (
              <button onClick={() => setConvo(which)} style={openConvoBtn}>Open conversation</button>
            )}
          </>
        ) : (
          <p style={quiet}>No {title.toLowerCase()} recorded.</p>
        )}
      </div>
    );
    return (
      <div style={wrap}>
        <button onClick={() => setSelected(null)} style={backLink}>
          <ArrowLeft size={14} /> Back to calendar
        </button>
        <h3 style={{ color: "#fff", fontSize: 18, margin: "12px 0 16px" }}>{selected}</h3>
        {entry ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Panel icon={<Sunrise size={16} color="#c9a24b" />} title="Login check-in"
              summary={entry.summary_morning} felt={entry.felt_morning}
              which="morning" hasConvo={Array.isArray(entry.morning_conversation) && entry.morning_conversation.length > 0} />
            <Panel icon={<Sunset size={16} color="#7f8bd6" />} title="Logout check-in"
              summary={entry.summary_evening} felt={entry.felt_evening}
              which="evening" hasConvo={Array.isArray(entry.evening_conversation) && entry.evening_conversation.length > 0} />
          </div>
        ) : (
          <p style={quiet}>No entry for this day.</p>
        )}
      </div>
    );
  }

  // ---- Calendar view (the page) ----
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div style={wrap}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
        <h2 style={{ color: "#fff", fontSize: 20, margin: 0 }}>My diary</h2>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button onClick={() => step(-1)} style={navBtn} aria-label="Previous month"><ChevronLeft size={18} /></button>
          <span style={{ color: "#fff", fontSize: 15, minWidth: 150, textAlign: "center" }}>{MONTHS[view.m]} {view.y}</span>
          <button onClick={() => step(1)} style={navBtn} aria-label="Next month"><ChevronRight size={18} /></button>
        </div>
      </div>

      <p style={{ display: "flex", alignItems: "center", gap: 6, color: "rgba(255,255,255,0.4)", fontSize: 12, marginBottom: 12 }}>
        <Lock size={12} /> Just for you, never shared. The tone marker is private, not a score.
      </p>

      {/* User-initiated: flag to manager any time. Sends only a generic signal, never diary words. */}
      <div style={{ marginBottom: 18 }}>
        {flagged ? (
          <span style={{ color: "rgba(255,255,255,0.5)", fontSize: 12 }}>
            Thanks. I have let your manager know a check-in might help. Nothing from your diary was shared.
          </span>
        ) : (
          <button
            onClick={flagToManager}
            style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 10, padding: "7px 12px", color: "rgba(255,255,255,0.6)", fontSize: 12, cursor: "pointer" }}
          >
            Flag to my manager
          </button>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 6, marginBottom: 8 }}>
        {WEEKDAYS.map((w) => (
          <div key={w} style={{ textAlign: "center", color: "rgba(255,255,255,0.35)", fontSize: 11, textTransform: "uppercase", letterSpacing: 1 }}>{w}</div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 6 }}>
        {cells.map((d, i) => {
          if (d === null) return <div key={`b${i}`} />;
          const k = keyOf(view.y, view.m, d);
          const entry = byDate[k];
          const tone = entry && entry.tone ? TONE[entry.tone] : null;
          const isToday = k === keyOf(today.getFullYear(), today.getMonth(), today.getDate());
          return (
            <button
              key={k}
              onClick={() => entry && setSelected(k)}
              disabled={!entry}
              style={{
                aspectRatio: "1 / 1",
                borderRadius: 12,
                border: `1px solid ${isToday ? "rgba(0,255,144,0.4)" : "rgba(255,255,255,0.07)"}`,
                background: entry ? "#111417" : "transparent",
                cursor: entry ? "pointer" : "default",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
                position: "relative",
              }}
            >
              <span style={{ color: entry ? "#fff" : "rgba(255,255,255,0.3)", fontSize: 14 }}>{d}</span>
              {/* underbracket in the day's tone colour */}
              {tone && (
                <span style={{ position: "absolute", bottom: 8, width: 18, height: 3, borderRadius: 2, background: tone.color }} />
              )}
            </button>
          );
        })}
      </div>

      {/* legend */}
      <div style={{ display: "flex", gap: 16, marginTop: 18 }}>
        {Object.entries(TONE).map(([k, t]) => (
          <div key={k} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 14, height: 3, borderRadius: 2, background: t.color }} />
            <span style={{ color: "rgba(255,255,255,0.5)", fontSize: 12 }}>{t.label}</span>
          </div>
        ))}
      </div>

      {!loading && !monthHasEntries && (
        <p style={{ ...quiet, marginTop: 24, textAlign: "center" }}>No diary entries this month yet.</p>
      )}
    </div>
  );
}

// ---- styles ----
const wrap = { maxWidth: 640, margin: "0 auto", padding: "8px 4px" };
const navBtn = { width: 34, height: 34, borderRadius: 10, border: "1px solid rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.04)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" };
const backLink = { display: "inline-flex", alignItems: "center", gap: 6, background: "transparent", border: "none", color: "rgba(255,255,255,0.6)", fontSize: 14, cursor: "pointer", padding: 0 };
const panel = { borderRadius: 14, border: "1px solid rgba(255,255,255,0.08)", background: "#0f1215", padding: 18 };
const openConvoBtn = { marginTop: 12, padding: "8px 14px", borderRadius: 10, border: "1px solid rgba(0,255,144,0.3)", background: "rgba(0,255,144,0.1)", color: "#00ff90", fontSize: 13, cursor: "pointer" };
const quiet = { color: "rgba(255,255,255,0.35)", fontSize: 14, fontStyle: "italic" };
